import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { 
  ProductDisambiguationResponse, 
  ProductSuggestion, 
  ExistingProductMatch,
  api 
} from '../services/api';

interface ProductDisambiguationProps {
  disambiguation: ProductDisambiguationResponse;
  originalContext?: string;
  onCancel: () => void;
}

const ProductDisambiguation = ({ 
  disambiguation, 
  originalContext, 
  onCancel 
}: ProductDisambiguationProps) => {
  const [selectedSuggestion, setSelectedSuggestion] = useState<ProductSuggestion | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const navigate = useNavigate();

  const handleSuggestionSelect = (suggestion: ProductSuggestion) => {
    setSelectedSuggestion(suggestion);
  };

  const handleExistingProductSelect = (match: ExistingProductMatch) => {
    // Navigate to existing product
    navigate(`/products/${match.slug}`);
  };

  const handleConfirmSelection = async () => {
    if (!selectedSuggestion) {
      toast.error('Please select a product first');
      return;
    }

    setIsAnalyzing(true);

    try {
      const result = await api.confirmProductAnalysis({
        selected_product: selectedSuggestion.name,
        brand: selectedSuggestion.brand,
        model: selectedSuggestion.model,
        category: selectedSuggestion.category,
        additional_context: originalContext
      });

      if (result.existing) {
        toast.success('Product found! Redirecting to analysis...');
        navigate(`/products/${result.slug}`);
      } else {
        toast.success('Analysis started! Redirecting to product page...');
        navigate(`/products/${result.slug}`);
      }
    } catch (error) {
      console.error('Product confirmation failed:', error);
      toast.error('Failed to start analysis. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleConfirmResolvedName = async () => {
    setIsAnalyzing(true);

    try {
      // Extract brand from resolved name or use a default
      const resolvedName = disambiguation.resolved_name;
      let brand = 'Unknown';
      let productName = resolvedName;
      
      // Try to extract brand from the resolved name
      const commonBrands = ['Apple', 'Samsung', 'Google', 'Microsoft', 'Sony', 'Nintendo', 'Tesla', 'BMW', 'Mercedes', 'Audi'];
      for (const commonBrand of commonBrands) {
        if (resolvedName.toLowerCase().includes(commonBrand.toLowerCase())) {
          brand = commonBrand;
          break;
        }
      }

      const result = await api.confirmProductAnalysis({
        selected_product: productName,
        brand: brand,
        model: '',
        category: 'consumer_goods', // Default category
        additional_context: originalContext
      });

      if (result.existing) {
        toast.success('Product found! Redirecting to analysis...');
        navigate(`/products/${result.slug}`);
      } else {
        toast.success('Analysis started! Redirecting to product page...');
        navigate(`/products/${result.slug}`);
      }
    } catch (error) {
      console.error('Product confirmation failed:', error);
      toast.error('Failed to start analysis. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="flex items-center mb-6">
          <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500 mr-3" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Product Disambiguation</h2>
            <p className="text-gray-600">{disambiguation.message}</p>
          </div>
        </div>

        {/* Resolved Name */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Our Best Guess:</h3>
          <p className="text-blue-800">
            <strong>{disambiguation.resolved_name}</strong>
            <span className="ml-2 text-sm text-blue-600">
              (Confidence: {disambiguation.confidence.toFixed(1)}%)
            </span>
          </p>
        </div>

        {/* Existing Products */}
        {disambiguation.existing_matches.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              📋 Existing Products in Database
            </h3>
            <div className="space-y-2">
              {disambiguation.existing_matches.map((match) => (
                <div
                  key={match.product_id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleExistingProductSelect(match)}
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      {match.brand ? `${match.brand} ${match.name}` : match.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {match.exact_match ? (
                        <span className="text-green-600 font-medium">✅ Exact Match</span>
                      ) : (
                        <span>Similarity: {match.similarity_score.toFixed(1)}%</span>
                      )}
                    </div>
                  </div>
                  <div className="text-blue-600">
                    View Analysis →
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Product Suggestions */}
        {disambiguation.suggestions.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              🎯 Specific Product Options
            </h3>
            <div className="space-y-3">
              {disambiguation.suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedSuggestion === suggestion
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => handleSuggestionSelect(suggestion)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <div className="font-medium text-gray-900">
                          {suggestion.brand} {suggestion.name}
                        </div>
                        {selectedSuggestion === suggestion && (
                          <CheckIcon className="h-5 w-5 text-blue-500 ml-2" />
                        )}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {suggestion.description}
                      </div>
                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        <span className="bg-gray-100 px-2 py-1 rounded">
                          {suggestion.category}
                        </span>
                        <span className="ml-2">
                          Confidence: {suggestion.confidence.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            ← Back to Search
          </button>
          
          <div className="flex space-x-3">
            {/* Show "Confirm Our Guess" button if no suggestions but we have a resolved name */}
            {disambiguation.suggestions.length === 0 && disambiguation.resolved_name && (
              <button
                onClick={handleConfirmResolvedName}
                disabled={isAnalyzing}
                className="inline-flex items-center px-6 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isAnalyzing ? (
                  <>
                    <div className="animate-spin w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
                    Starting Analysis...
                  </>
                ) : (
                  <>
                    ✓ Confirm "{disambiguation.resolved_name}"
                  </>
                )}
              </button>
            )}
            
            {selectedSuggestion && (
              <button
                onClick={handleConfirmSelection}
                disabled={isAnalyzing}
                className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isAnalyzing ? (
                  <>
                    <div className="animate-spin w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                    Starting Analysis...
                  </>
                ) : (
                  <>
                    Analyze {selectedSuggestion.brand} {selectedSuggestion.name}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDisambiguation; 